import { recordTrackingEvent, hasTrackingConsent } from '@/services/trackingService';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const trackEvent = (eventName: string, payload: any = {}) => {
  if (typeof window !== 'undefined') {
    // Only fire dataLayer events and record tracking if user explicitly granted consent
    if (!hasTrackingConsent()) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    
    // Add page_location automatically
    const page_location = window.location.pathname;
    
    window.dataLayer.push({
      event: eventName,
      page_location,
      ...payload
    });
    console.log(`[Analytics] ${eventName}`, { page_location, ...payload });

    // Determine type (form vs click)
    const isForm = eventName.includes('form') || 
                   eventName.includes('signup') || 
                   eventName.includes('request') || 
                   eventName.includes('complete') || 
                   eventName.includes('captured') ||
                   eventName.includes('download_submit');

    const label = payload.form_name || payload.file_name || payload.label || eventName;
    const email = payload.email || undefined;
    const targetUrl = payload.targetUrl || payload.href || undefined;
    const ctaPosition = payload.cta_position || payload.location || undefined;

    // Send to Google Sheets / Firestore tracking pipeline asynchronously
    recordTrackingEvent({
      type: isForm ? 'form' : 'click',
      eventName,
      label: String(label),
      targetUrl,
      pageUrl: page_location,
      email,
      ctaPosition,
      details: payload
    }).catch(err => console.warn('Could not record tracking event:', err));
  }
};

// Form Events
export const trackFormStart = (formName: string, cta_position: string) => 
  trackEvent('form_start', { form_name: formName, cta_position });

export const trackFormSubmit = (formName: string, cta_position: string) => 
  trackEvent('form_submit', { form_name: formName, cta_position });

// Specific Lead Events
export const trackSampleReportRequest = (cta_position: string) => trackEvent('sample_report_request', { cta_position });
export const trackMiniAssessmentStart = (cta_position: string = 'mini_assessment') => trackEvent('mini_assessment_start', { cta_position });
export const trackMiniAssessmentCompleted = () => trackEvent('mini_assessment_completed');
export const trackMiniAssessmentSubmit = () => trackEvent('mini_assessment_submit');
export const trackMiniAssessmentResultView = (primary_pattern: number) => trackEvent('mini_assessment_result_view', { primary_pattern });
export const trackMiniAssessmentComplete = (cta_position: string) => trackEvent('mini_assessment_complete', { cta_position });
export const trackNewsletterSignup = (cta_position: string) => trackEvent('newsletter_signup', { cta_position });
export const trackPricingGuideDownload = (cta_position: string) => trackEvent('pricing_guide_download', { cta_position });
export const trackCertificateBrochureDownload = (cta_position: string) => trackEvent('certificate_brochure_download_submit', { cta_position });
export const trackTeamBrochureDownload = (cta_position: string) => trackEvent('team_brochure_download_submit', { cta_position });

// Booking Events
export const trackBookingWidgetView = (cta_position: string) => trackEvent('booking_widget_view', { cta_position });
export const trackBookingSlotSelected = (cta_position: string) => trackEvent('booking_slot_selected', { cta_position });

// Interactions
export const trackIcfExplainerView = (cta_position: string) => trackEvent('icf_explainer_view', { cta_position });
export const trackDirectorySearch = (cta_position: string) => trackEvent('directory_search', { cta_position });
export const trackFileDownload = (fileName: string, cta_position: string) => trackEvent('file_download', { file_name: fileName, cta_position });
export const trackMailtoClick = (cta_position: string) => trackEvent('mailto_click', { cta_position });
export const trackWhatsappClick = (cta_position: string) => trackEvent('whatsapp_click', { cta_position });
export const trackPhoneClick = (cta_position: string) => trackEvent('phone_click', { cta_position });

// Legacy mappings (keeping these so we don't break existing imports, but adding cta_position where possible)
export const trackDiagnosticStart = () => trackEvent('diagnostic_start', { cta_position: 'legacy' });
export const trackDiagnosticComplete = (score: number) => trackEvent('diagnostic_complete', { score, cta_position: 'legacy' });
export const trackEmailCaptured = (email: string) => trackEvent('email_captured', { cta_position: 'legacy' });
export const trackRegistrationClick = (location: string) => trackEvent('registration_click', { cta_position: location });
export const trackDiscoveryCallClick = (location: string = 'generic') => trackEvent('discovery_call_click', { cta_position: location });
export const trackPricingView = () => trackEvent('pricing_view', { cta_position: 'legacy' });
export const trackCohortDateView = () => trackEvent('cohort_date_view', { cta_position: 'legacy' });
